import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import { PermissoesProvider } from './hooks/usePermissoes';
import { authService } from './services/auth';
import Login from './pages/auth/Login';
import Layout from './components/layout/Layout';
import Dashboard from './pages/dashboard/Dashboard';
import VeiculosList from './pages/veiculos/VeiculosList';
import VeiculosForm from './pages/veiculos/VeiculosForm';
import VeiculoDetail from './pages/veiculos/VeiculoDetail';
import MotoristasList from './pages/motoristas/MotoristasList';
import MotoristasForm from './pages/motoristas/MotoristasForm';
import MotoristaDetail from './pages/motoristas/MotoristaDetail';
import AbastecimentosList from './pages/abastecimentos/AbastecimentosList';
import AbastecimentosForm from './pages/abastecimentos/AbastecimentosForm';
import AbastecimentoDetail from './pages/abastecimentos/AbastecimentoDetail';
import ManutencoesList from './pages/manutencoes/ManutencoesList';
import ManutencoesForm from './pages/manutencoes/ManutencoesForm';
import ManutencaoDetail from './pages/manutencoes/ManutencaoDetail';
import DespesasList from './pages/despesas/DespesasList';
import DespesasForm from './pages/despesas/DespesasForm';
import DespesaDetail from './pages/despesas/DespesaDetail';
import CategoriasDespesa from './pages/despesas/CategoriasDespesa';
import ViagensList from './pages/viagens/ViagensList';
import ViagensForm from './pages/viagens/ViagensForm';
import ViagemDetail from './pages/viagens/ViagemDetail';
import RelatoriosDashboard from './pages/relatorios/RelatoriosDashboard';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import UsuariosList from './pages/usuarios/UsuariosList';
import CadastroMotorista from './pages/auth/CadastroMotorista';
import EsqueciSenha from './pages/auth/EsqueciSenha';
import RedefinirSenha from './pages/auth/RedefinirSenha';
import AprovacoesList from './pages/aprovacoes/AprovacoesList';
import AlertasList from './pages/alertas/AlertasList';
import PortalMotorista from './pages/motorista/PortalMotorista';
import LancamentoMotorista from './pages/motorista/LancamentoMotorista';
import ChecklistDeViagem from './pages/motorista/ChecklistDeViagem';
import AuditoriaList from './pages/auditoria/AuditoriaList';
import TrocarSenha from './pages/auth/TrocarSenha';
import PermissoesList from './pages/permissoes/PermissoesList';
import NotificacoesConfig from './pages/notificacoes/NotificacoesConfig';
import Exige from './components/Exige';

// Guard de rota privada (área administrativa / gestão)
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  if (!authService.isAuthenticated()) {
    // Guarda o destino pretendido. Sem isto, quem clica no botão de um e-mail de
    // notificação com a sessão expirada faz login e cai no dashboard, tendo que
    // procurar a tela sozinho — justamente quando o link mais serviria. Vale para
    // qualquer link profundo, inclusive um favorito salvo pelo usuário.
    const destino = location.pathname + location.search;

    return <Navigate to="/login" state={{ de: destino }} replace />;
  }
  // Senha ainda provisória: o servidor recusa todas as demais rotas, então mandar
  // para a tela de troca evita uma sequência de telas vazias com erro 403.
  if (authService.getUser()?.deve_trocar_senha) {
    return <Navigate to="/trocar-senha" replace />;
  }
  // Motorista não acessa a gestão — vai para o próprio portal
  if (authService.getUser()?.role === 'motorista') {
    return <Navigate to="/motorista" replace />;
  }
  return <>{children}</>;
};

// Guard do Portal do Motorista
const MotoristaRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (authService.getUser()?.deve_trocar_senha) {
    return <Navigate to="/trocar-senha" replace />;
  }
  if (authService.getUser()?.role !== 'motorista') {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

// Configuracao do React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <PermissoesProvider>
        <ConfirmProvider>
        <div className="App">
          <Routes>
            {/* Rotas publicas de autenticacao */}
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro-motorista" element={<CadastroMotorista />} />
            <Route path="/esqueci-senha" element={<EsqueciSenha />} />
            <Route path="/redefinir-senha" element={<RedefinirSenha />} />

            {/* Troca obrigatória da senha provisória: exige sessão, mas fica fora
                do layout, porque nada mais do sistema responde até a troca. */}
            <Route path="/trocar-senha" element={<TrocarSenha />} />

            {/* Portal do Motorista (login próprio, layout próprio) */}
            <Route path="/motorista" element={<MotoristaRoute><PortalMotorista /></MotoristaRoute>} />
            <Route path="/motorista/lancar/:tipo" element={<MotoristaRoute><LancamentoMotorista /></MotoristaRoute>} />
            {/* Tela propria, e nao um modal na lista: sao 25 itens com
                observacao e foto, e o motorista preenche de pe ao lado do
                caminhao — precisa da altura inteira do celular. */}
            <Route path="/motorista/viagem/:id/checklist" element={<MotoristaRoute><ChecklistDeViagem /></MotoristaRoute>} />

            {/* Rota raiz redireciona para dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Layout principal com rotas aninhadas (protegidas) */}
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route path="dashboard" element={<Dashboard />} />
              
                              {/* Rotas de Veículos */}
        <Route path="veiculos" element={<Exige modulo="veiculos" acao="consultar"><VeiculosList /></Exige>} />
        <Route path="veiculos/novo" element={<Exige modulo="veiculos" acao="criar"><VeiculosForm /></Exige>} />
        <Route path="veiculos/:id" element={<Exige modulo="veiculos" acao="consultar"><VeiculoDetail /></Exige>} />
        <Route path="veiculos/:id/editar" element={<Exige modulo="veiculos" acao="editar"><VeiculosForm /></Exige>} />
        
        {/* Rotas de Motoristas */}
        <Route path="motoristas" element={<Exige modulo="motoristas" acao="consultar"><MotoristasList /></Exige>} />
        <Route path="motoristas/novo" element={<Exige modulo="motoristas" acao="criar"><MotoristasForm /></Exige>} />
        <Route path="motoristas/:id" element={<Exige modulo="motoristas" acao="consultar"><MotoristaDetail /></Exige>} />
        <Route path="motoristas/:id/editar" element={<Exige modulo="motoristas" acao="editar"><MotoristasForm /></Exige>} />
        
                {/* Rotas de Abastecimentos */}
        <Route path="abastecimentos" element={<Exige modulo="abastecimentos" acao="consultar"><AbastecimentosList /></Exige>} />
        <Route path="abastecimentos/novo" element={<Exige modulo="abastecimentos" acao="criar"><AbastecimentosForm /></Exige>} />
        <Route path="abastecimentos/:id" element={<Exige modulo="abastecimentos" acao="consultar"><AbastecimentoDetail /></Exige>} />
        <Route path="abastecimentos/:id/editar" element={<Exige modulo="abastecimentos" acao="editar"><AbastecimentosForm /></Exige>} />
        
        {/* Rotas de Manutenções */}
        <Route path="manutencoes" element={<Exige modulo="manutencoes" acao="consultar"><ManutencoesList /></Exige>} />
        <Route path="manutencoes/novo" element={<Exige modulo="manutencoes" acao="criar"><ManutencoesForm /></Exige>} />
        <Route path="manutencoes/:id" element={<Exige modulo="manutencoes" acao="consultar"><ManutencaoDetail /></Exige>} />
        <Route path="manutencoes/:id/editar" element={<Exige modulo="manutencoes" acao="editar"><ManutencoesForm /></Exige>} />
              <Route path="despesas" element={<Exige modulo="despesas" acao="consultar"><DespesasList /></Exige>} />
              <Route path="despesas/novo" element={<Exige modulo="despesas" acao="criar"><DespesasForm /></Exige>} />
              {/* O catalogo da empresa. Fica sob despesas porque quem cuida do
                  gasto e quem organiza como ele e classificado — e a rota do
                  servidor segue a mesma permissao. */}
              <Route path="despesas/categorias" element={<Exige modulo="despesas" acao="consultar"><CategoriasDespesa /></Exige>} />
              <Route path="despesas/:id" element={<Exige modulo="despesas" acao="consultar"><DespesaDetail /></Exige>} />
              <Route path="despesas/:id/editar" element={<Exige modulo="despesas" acao="editar"><DespesasForm /></Exige>} />
              <Route path="viagens" element={<Exige modulo="viagens" acao="consultar"><ViagensList /></Exige>} />
              <Route path="viagens/novo" element={<Exige modulo="viagens" acao="criar"><ViagensForm /></Exige>} />
              <Route path="viagens/:id" element={<Exige modulo="viagens" acao="consultar"><ViagemDetail /></Exige>} />
              <Route path="viagens/:id/editar" element={<Exige modulo="viagens" acao="editar"><ViagensForm /></Exige>} />
              <Route path="relatorios" element={<Exige modulo="relatorios" acao="consultar"><RelatoriosDashboard /></Exige>} />
              <Route path="alertas" element={<AlertasList />} />
              <Route path="documentos" element={<div className="p-6"><h1 className="text-2xl font-bold">Documentos (em construção)</h1></div>} />
              <Route path="configuracoes" element={<div className="p-6"><h1 className="text-2xl font-bold">Configurações (em construção)</h1></div>} />
              <Route path="usuarios" element={<Exige modulo="usuarios" acao="consultar"><UsuariosList /></Exige>} />
        <Route path="auditoria" element={<Exige modulo="auditoria" acao="consultar"><AuditoriaList /></Exige>} />
        <Route path="permissoes" element={<Exige modulo="usuarios" acao="editar"><PermissoesList /></Exige>} />
        <Route path="notificacoes" element={<Exige modulo="notificacoes" acao="consultar"><NotificacoesConfig /></Exige>} />
              <Route path="aprovacoes" element={<Exige modulo="aprovacoes" acao="consultar"><AprovacoesList /></Exige>} />
              <Route path="super-admin" element={<SuperAdminDashboard />} />
            </Route>
            
            {/* Rota 404 */}
            <Route path="*" element={<div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-secondary-900 mb-4">404</h1>
                <p className="text-secondary-600 mb-4">Página não encontrada</p>
                <button 
                  onClick={() => window.history.back()}
                  className="btn-primary"
                >
                  Voltar
                </button>
              </div>
            </div>} />
          </Routes>
          
          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#059669',
                },
              },
              error: {
                style: {
                  background: '#DC2626',
                },
              },
            }}
          />
        </div>
        </ConfirmProvider>
        </PermissoesProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
